import { DigitalVisitReasonEnum } from "@/lib/__generated/api";
import type { BffFacilityFieldsFragment } from "@/lib/__generated/beffe.sdk";
import type { AppointmentData, SchedulerContext } from "@/types/scheduler-machine";

import { schedulerSetup } from "./schedulerSetup";
import { appointmentReviewState } from "./states/appointmentReviewState";
import { dateTimeSelectionState } from "./states/dateTimeSelectionState";
import { guarantorInformationState } from "./states/guarantorInformationState";
import { patientInformationState } from "./states/patientInformationState";
import { reasonForVisitState } from "./states/reasonForVisitState";

const DEFAULT_REST_URL = "https://api.aspendental.com/api/web/";

function createInitialContext(
  facility: BffFacilityFieldsFragment | null = null,
  stateCode: string = "",
  restUrl: string = DEFAULT_REST_URL,
  recaptchaSiteKey: string = "",
  enableRecaptcha: boolean = false,
  disableExternalConfirmation: boolean = false,
  preselectedAppointment?: Partial<AppointmentData>,
): SchedulerContext {
  const hasPreselected = !!preselectedAppointment?.slotId;
  return {
    reasonForVisit: null,
    initialReason: null,
    patientData: {
      firstName: "",
      lastName: "",
      dateOfBirth: "",
      emailAddress: "",
      phoneNumber: "",
      dentalInsurance: "",
      smsMarketingConsent: true,
      smsTransactionalConsent: true,
    },
    guarantorData: {
      firstName: "",
      lastName: "",
      dateOfBirth: "",
      guarantorZipCode: "",
      email: "",
      guarantorPhoneNumber: "",
      relationshipToPatient: "",
    },
    validatedPatientResponse: null,
    patientValidationRequested: false,
    questionnaire: null,
    needBasedQuestionsAnswers: undefined,
    needBasedQuestionsValid: false,
    patientSubmitAttempted: false,
    appointmentData: hasPreselected ? preselectedAppointment : {},
    facility,
    formErrors: {},
    errors: {},
    // Mark dateTimeSelection complete so GOTO_STEP allows back-nav and the wizard skips it.
    completedSteps: hasPreselected ? { dateTimeSelection: true } : {},
    slots: [],
    nearbySlots: [],
    nearbySlotsPending: false,
    slotsError: null,
    slotsSearchDate: null,
    currentLanguage: "en-us",
    stateCode,
    restUrl,
    recaptchaSiteKey,
    enableRecaptcha,
    disableExternalConfirmation,
    scheduleAppointmentResponse: null,
  };
}

export const schedulerMachine = schedulerSetup.createMachine({
  id: "aspen-scheduler",
  initial: "reasonForVisit",
  context: ({ input }) => {
    const initialContext = createInitialContext(
      input.facility,
      input.stateCode,
      input.restUrl,
      input.recaptchaSiteKey,
      input.enableRecaptcha,
      input.disableExternalConfirmation,
      input.preselectedAppointment,
    );
    return {
      ...initialContext,
      reasonForVisit: input.initialReason ?? null,
      initialReason: input.initialReason ?? null,
      patientData:
        input.initialReason === DigitalVisitReasonEnum.HealthyMouthMovement
          ? { ...initialContext.patientData, dentalInsurance: "false" }
          : initialContext.patientData,
    };
  },
  on: {
    SYNC_FACILITY: {
      actions: {
        type: "assignFacilityAndStateCode",
        params: ({ event }) => ({ facility: event.facility, stateCode: event.stateCode }),
      },
    },
    CLEAR_ERROR: {
      // Dismissing a dialog clears transient errors only; a refusal stays in
      // appointmentReview.refused, so Confirm stays disabled.
      actions: { type: "clearErrors" },
    },
    CLEAR_FIELD_ERROR: {
      actions: {
        type: "clearFieldError",
        params: ({ event }) => ({ key: event.key, field: event.field }),
      },
    },
    // Leaving appointmentReview clears its validation verdict (see its `exit`).
    GOTO_STEP: [
      {
        guard: { type: "canGoToStep", params: { step: "patientInformation" } },
        target: ".patientInformation",
      },
      {
        guard: { type: "canGoToStep", params: { step: "dateTimeSelection" } },
        target: ".dateTimeSelection",
      },
      {
        guard: { type: "canGoToStep", params: { step: "guarantorInformation" } },
        target: ".guarantorInformation",
      },
    ],
    SET_LANGUAGE: {
      actions: {
        type: "assignLanguage",
        params: ({ event }) => ({ language: event.language }),
      },
    },
  },
  states: {
    reasonForVisit: reasonForVisitState,

    patientInformation: patientInformationState,

    dateTimeSelection: dateTimeSelectionState,

    guarantorInformation: guarantorInformationState,

    appointmentReview: appointmentReviewState,

    confirmation: {
      type: "final",
      meta: { componentKey: "confirmation", label: "confirmation", showFooter: false },
    },
  },
});
